import { AGClientSocket, create } from "socketcluster-client";
import { IReactionDisposer, when } from "mobx";
import { auth, orgUser } from "../../../../../../common";
import { getLoginPath, redirectToLogin } from "../../../util";
import { AbstractClientApi, IApiData } from "./AbstractClientApi";
import { AuthenticationError } from "../../../../../../modules/dist/core/error/aaa";

// --

// IAuthRefreshOptions
export interface IAuthRefreshOptions {
	authUrl?: string;
	refreshUrl?: string;
	refreshInterval?: number;
}

// IApiOpts
interface IApiDeps extends AGClientSocket.ClientOptions, IAuthRefreshOptions {}

/**
 * Apiimport
 */
export class Api extends AbstractClientApi<IApiData> {
	/**
	 * Check support for LS
	 */
	protected static checkLocalStorageEnabled() {
		let err: Error;

		try {
			// Some browsers will throw an error here if ge is disabled.
			window.localStorage;

			// Safari, in Private Browsing Mode, looks like it supports localStorage but all calls to setItem
			// throw QuotaExceededError. We're going to detect this and avoid hard to debug edge cases.
			window.localStorage.setItem("__scLocalStorageTest", "1");
			window.localStorage.removeItem("__scLocalStorageTest");
		} catch (e) {
			err = e as any;
		}
		return !err;
	}

	public authUrl: string;
	public refreshUrl: string;
	public refreshInterval: number;
	protected refreshTimer: number;

	protected isLocalStorageEnabled: boolean;

	/**
	 * Class constructor
	 * @param opts
	 */
	public constructor(deps: IApiDeps = {}) {
		super({
			authState: null,
			state: null,
		});

		deps.authTokenName || (deps.authTokenName = "socketcluster.authToken");
		console.log("Creating socket", deps);

		this.__socket = create(deps);

		this.isLocalStorageEnabled = Api.checkLocalStorageEnabled();

		this.authUrl = typeof deps.authUrl === "string" ? deps.authUrl : "/auth/login";
		this.refreshUrl = typeof deps.refreshUrl === "string" ? deps.refreshUrl : "/auth/access";
		this.refreshInterval = Number.isInteger(deps.refreshInterval) && deps.refreshInterval > 0 ? deps.refreshInterval : 60 * 60 * 1000; // refresh token every hour

		(window as any).switchOrg = async (orgId: string) => await this.switchOrg(orgId);
		(window as any).redirectToLogin = (preservePath = true) => this.redirectToLogin(preservePath);

		this.init();
	}

	/**
	 * Invoke
	 * @param event
	 * @param req
	 * @returns
	 */
	public invoke<R, D>(event: string, req: R): Promise<D> {
		return super.invokeRaw(event, req);
	}

	/**
	 * Transmit
	 * @param event
	 * @param req
	 * @returns
	 */
	public transmit<T>(event: string, req: T): Promise<void> {
		return super.transmitRaw(event, req);
	}

	/**
	 * Init
	 */
	protected init() {
		(async () => {
			for await (let data of this.socket.listener("connecting")) {
				console.log("Socket connecting...");
				this.onStateChange();
			}
		})();

		(async () => {
			for await (let data of this.socket.listener("connect")) {
				console.log("Socket connected");
				this.onStateChange();
			}
		})();

		(async () => {
			for await (let data of this.socket.listener("disconnect")) {
				console.log("Socket disconnected");
				this.onStateChange();
			}
		})();

		(async () => {
			for await (let data of this.socket.listener("authStateChange")) {
				console.log("Socket auth status changed", data);
				this.onAuthStateChange(data);
			}
		})();

		(async () => {
			for await (let data of this.socket.listener("error")) {
				console.error("Socket error", data);
			}
		})();

		(async () => {
			for await (let data of this.socket.listener("subscribe")) {
				console.log("Channel subscribed", data);
			}
		})();

		(async () => {
			for await (let data of this.socket.listener("subscribeFail")) {
				console.error("Channel subscribe fail", data);
			}
		})();

		(async () => {
			for await (let data of this.socket.listener("unsubscribe")) {
				console.warn("Channel unsubscribed", data);
			}
		})();
	}

	/**
	 * Fetch identity
	 * @returns
	 */
	public async fetchIdentity() {
		if (!this.authenticated) throw new AuthenticationError("Socket must be authenticated");

		const identity = await this.globalCustomAction<void, orgUser.membership.Identity>("user.FetchIdentity", void 0);
		if (!identity.org) throw new AuthenticationError("You are no longer member of an organization!");

		console.log("<<< Fetched identity", identity);
		this.__identity = identity;

		/*
		if (identity.clientActions) {
			if (identity.clientActions.redirectUrl) window.location.assign(identity.clientActions.redirectUrl);
		}
			*/

		return identity;
	}

	public onAppStart(appName: string) {
		if (this.authenticated) {
			this.transmit("onAppStart", { appName });
		}
	}

	/**
	 * Connect ws
	 * @param arg
	 * @returns
	 */
	public async connect(opts?: AGClientSocket.ClientOptions): Promise<void> {
		console.log("WS Connecting...");

		opts || (opts = {});

		// Create promise
		const p = new Promise<void>((resolve, reject) => {
			let d: IReactionDisposer;

			// reaction
			d = when(
				() => this.connected,
				async () => {
					d();
					await this.createToaster();
					resolve();
				},
				{
					onError: () => {
						d();
						reject(new Error("WS connect timeout"));
					},
					timeout: opts.connectTimeout,
				}
			);

			this.socket.connect(opts);
		});

		return p;
	}

	/**
	 * Authenticate ws
	 */
	public async wsAuthenticate(opts?: { fetchIdentity?: boolean; timeout?: number }) {
		opts || (opts = {});
		Number.isInteger(opts.timeout) || (opts.timeout = 50000);
		typeof opts.fetchIdentity === "boolean" || (opts.fetchIdentity = true);

		if (this.authenticated) {
			console.log("WS already authenticated");
			return void 0;
		}

		if (!this.connected) await this.connect();

		if (this.authenticated) {
			opts.fetchIdentity && (await this.fetchIdentity());
			return void 0;
		}

		try {
			console.log(">> WS authenticating...");
			const { savedToken } = this;
			if (!savedToken) throw new AuthenticationError("No saved token available");

			const res = await this.socket.authenticate(savedToken);
			if (res.authError) {
				console.log("Auth error", res.authError);
				throw new AuthenticationError(res.authError.message);
			}
			if (!res.isAuthenticated) {
				throw new AuthenticationError("Authentication error");
			}

			console.log(">> WS authenticated, fetching identity...");
			await this.fetchIdentity();
		} catch (err) {
			console.warn("Socket auth error", err);
			this.redirectToLogin(true);
		}
	}

	/**
	 * State change
	 */
	protected onStateChange = () => {
		this.set("state", this.socket.state);
	};

	/**
	 * Auth state change
	 */
	protected onAuthStateChange = (data: AGClientSocket.AuthStateChangeData) => {
		this.set("authState", data.newAuthState);

		if (data.oldAuthState === this.socket.AUTHENTICATED && data.newAuthState === this.socket.UNAUTHENTICATED) {
			this.deauthenticate();
			window.location.reload();
		} else if (data.oldAuthState === this.socket.UNAUTHENTICATED && data.newAuthState === this.socket.AUTHENTICATED) {
			console.log("Socket authenticated");
			this.scheduleRefreshToken();
		}
	};

	public get savedToken() {
		console.warn("savedToken", this.socket.authTokenName, this.isLocalStorageEnabled, window.localStorage.getItem(this.socket.authTokenName));
		return this.isLocalStorageEnabled ? window.localStorage.getItem(this.socket.authTokenName) : void 0;
	}

	/**
	 * Save token
	 * @param token
	 */
	protected saveToken(token: string) {
		if (this.isLocalStorageEnabled) {
			console.log("Saving token", token);
			window.localStorage.setItem(this.socket.authTokenName, token);
		}
	}

	/**
	 * Remove token
	 */
	protected removeToken() {
		if (this.isLocalStorageEnabled) {
			window.localStorage.removeItem(this.socket.authTokenName);
		}
	}

	/**
	 * Fetch wrapper
	 * @param url
	 * @param init
	 */
	public fetch(url: string, init?: RequestInit) {
		if (this.socket.signedAuthToken) {
			init || (init = {});
			init.headers || (init.headers = new Headers());

			if (init.headers instanceof Headers) {
				init.headers.set("Authorization", `Bearer ${this.socket.signedAuthToken}`);
			} else if (Array.isArray(init.headers)) {
				init.headers.push(["Authorization: Bearer", this.socket.signedAuthToken]);
			} else {
				(init.headers as any)["Authorization"] = `Bearer ${this.socket.signedAuthToken}`;
			}
		}

		return fetch(url, init);
	}

	/**
	 * Refresh token
	 */
	protected refreshToken(): Promise<string> {
		if (!this.socket.signedAuthToken) {
			this.redirectToLogin(false);
		}

		console.log("Refreshing token");

		return new Promise<string>((resolve, reject) => {
			this.fetch(this.refreshUrl, {
				method: "GET",
				cache: "no-cache",
			})
				.then((res) => {
					if (res.status !== 200) throw new AuthenticationError("Authentication error " + res.status);
					return res.json();
				})
				.then((signedToken: auth.ISignedToken) => {
					// Authenticate with the new token
					console.log("Authenticating with new token");

					this.saveToken(signedToken.token);
					this.socket.authenticate(signedToken.token);

					resolve(signedToken.token);
				})
				.catch((err) => reject(err));
		});
	}

	/**
	 * Schedule refresh token
	 */
	protected scheduleRefreshToken() {
		window.clearTimeout(this.refreshTimer);
		console.log("Schedule token refresh", this.refreshInterval);

		this.refreshTimer = window.setTimeout(() => {
			this.refreshToken()
				.then(() => {
					this.scheduleRefreshToken();
				})
				.catch((err) => {
					console.error(err);
				});
		}, Math.floor(this.refreshInterval));
	}

	/**
	 * Login user, receive tuple and save token
	 * @param username
	 * @param password
	 */
	public login(username: string, password: string): Promise<string> {
		if (typeof username !== "string" || !username) return Promise.reject(new Error("Invalid username"));
		if (typeof password !== "string" || !password) return Promise.reject(new Error("Invalid password"));

		console.log("Authenticating...");
		return new Promise<string>((resolve, reject) => {
			const headers = new Headers();
			headers.append("Content-Type", "application/json");
			headers.append("Accept", "application/json");

			fetch(this.authUrl, {
				method: "POST",
				cache: "no-cache",
				headers,
				body: JSON.stringify({
					username,
					password,
				}),
			} as RequestInit)
				.then((res) => {
					console.log("Authentication response", res);

					if (res.status !== 200) throw new AuthenticationError("Authentication error " + res.status);
					else return res.json();
				})
				.then((signedToken: auth.ISignedToken) => {
					console.log("signedToken", JSON.stringify(signedToken));
					this.saveToken(signedToken.token);
					resolve(signedToken.token);
				})
				.catch((err) => {
					console.error("Login error", err);
					reject(err);
				});
		});
	}

	/**
	 * Deauth
	 */
	public deauthenticate() {
		console.log("deauthenticating...");
		window.clearTimeout(this.refreshTimer);
		this.removeToken();
		return this.socket.deauthenticate();
	}

	/**
	 * Set password
	 * @param password
	 */
	public changePassword(password: string) {
		return this.globalCustomAction(
			"user.ChangePassword",
			{
				id: this.authToken.uid,
				password,
			},
			true
		);
	}

	/**
	 * Clear user preferences
	 */
	public clearPrefs() {
		return this.globalCustomAction("user.prefs.Clear", { id: this.authToken.uid }, true);
	}

	/**
	 * Redirect to login
	 * @param preservePath
	 * @param reason
	 */
	public redirectToLogin(preservePath = true, reason?: string) {
		const isDevMode = location.search.includes("/dev");

		if (preservePath) {
			redirectToLogin(reason);
		} else {
			window.location.assign(getLoginPath());
		}
	}

	/**
	 * Switch org
	 * @param orgId
	 */
	public async switchOrg(orgId: string, reload = true) {
		console.log("Switch to org >>>", orgId);

		const res = await this.globalCustomAction<{ id: string; pathname: string }, { token: string; url: string }>(
			"user.SwitchOrg",
			{ id: orgId, pathname: location.pathname },
			true
		);

		console.log("Switch to org <<<", res);
		this.saveToken(res.token);

		reload && location.reload();
	}
}
